import { accountRegistrationValidations } from './validations';
import BaseTemplate from '../../components/baseTemplate';
import { HttpStatusCode } from '../../../../utils/enums/http-method.enum';
import Input from '../../../../components/forms/input';
import { LoginService } from '../../../../services/login.service';
import Spinner from '../../../../components/ui/spinner';
import { useFormik } from 'formik';
import { useLocalization } from '../../../../utils/useLocalization';
import { useTranslation } from 'react-i18next';
import { getFooterLink, getPasswordFieldDesc } from 'src/utils/helpers';
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './styles.scss';

const AccountRegistration = () => {
    const { accountRegistration } = useLocalization();
    const [searchParams] = useSearchParams();
    const navigation = useNavigate();
    const [code, setCode] = useState('');
    const { i18n } = useTranslation();
    const lang = i18n?.language?.includes('fr') ? 'fr' : 'en';
    const usageUrl = getFooterLink('usage', lang);

    useEffect(() => {
        if (searchParams && [...searchParams][0] && [...searchParams][0][0]) {
            const params = [...searchParams][0][0];
            if (params) setCode(params);
        } else setNetworkError(true);
    }, [searchParams]);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [loading, setLoading] = useState<boolean>(false);
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [showPasswordConfirm, setShowPasswordConfirm] =
        useState<boolean>(false);
    const [agreement, setAgreement] = useState<boolean>(false);
    const [networkError, setNetworkError] = useState<boolean>(false);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [submitErrors, setSubmitErrors] = useState({
        password: '',
        passwordConfirm: '',
    });
    const {
        values,
        errors,
        touched,
        handleChange,
        handleSubmit,
        handleBlur,
        setFieldTouched,
    } = useFormik({
        initialValues: {
            password: '',
            passwordConfirm: '',
        },
        validationSchema: accountRegistrationValidations,
        onSubmit: handlerSubmit,
    });
    async function handlerSubmit() {
        try {
            if (code && agreement) {
                setLoading(true);
                const { password } = values;
                const service = new LoginService();
                const res = await service.accountRegistration(code, password);
                if (res?.data) {
                    const { message, statusCode } = res.data;
                    if (
                        statusCode === HttpStatusCode.SUCCESS ||
                        message.includes('registered')
                    )
                        navigation('/registration-success');
                    else setNetworkError(true);
                }
            } else {
                setNetworkError(true);
            }
        } catch (e) {
            setNetworkError(true);
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => {
        const handleLanguageChange = () => {
            Object.keys(touched).forEach((field) => {
                setFieldTouched(field, true, true);
            });
        };

        i18n.on('languageChanged', handleLanguageChange);
        return () => {
            i18n.off('languageChanged', handleLanguageChange);
        };
    }, [i18n, touched, errors, setFieldTouched]);

    return (
        <BaseTemplate>
            <div className={'account-registration'}>
                <div className={'header u-mb-xxsmall u-mt-xlarge'}>
                    <h4>{accountRegistration.ACC_REG_TITLE}</h4>
                    {networkError && (
                        <section
                            id='js-notifications-group'
                            className='c-notifications-group  js-panelgroup js-panelgroup js-is-fx is-stacked js-is-ready u-mb-small u-mt-xsmall'
                        >
                            <div
                                className={
                                    'c-notification__container  js-notification-container'
                                }
                            >
                                <div className='c-notification  c-notification--error'>
                                    <div className='c-notification__outer-wrap'>
                                        <div className='c-notification__inner-wrap'>
                                            <div className='c-notification__content'>
                                                <p className='c-notification__text'>
                                                    {
                                                        accountRegistration.ACC_REG_LABEL_NOTIFICATION_ERROR
                                                    }
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>
                    )}
                    <p className={'description u-mt-xsmall'}>
                        {accountRegistration.ACC_REG_DESCRIPTION}
                    </p>
                    <p className={'description-fields u-mt-xsmall'}>
                        <span>*</span>
                        {accountRegistration.ACC_REG_LABEL_FIELD_REQUIRED}
                    </p>
                </div>
                <form
                    onSubmit={handleSubmit}
                    autoComplete={'off'}
                    className={'u-mt-xxsmall registrationForm'}
                >
                    <Input
                        labelName={accountRegistration.ACC_REG_LABEL_PASSWORD}
                        type={showPassword ? 'text' : 'password'}
                        name={'password'}
                        icon={'eye'}
                        handlerChange={handleChange}
                        handlerBlur={handleBlur}
                        handlerIcon={() => {
                            setShowPassword(!showPassword);
                        }}
                        value={values.password}
                        asErrors={errors.password}
                        asTouched={touched.password}
                        asSubmitError={submitErrors.password}
                        description={getPasswordFieldDesc(
                            values.password,
                            accountRegistration,
                        )}
                        disabled={!code}
                    />
                    <Input
                        labelName={
                            accountRegistration.ACC_REG_LABEL_PASSWORD_CONFIRM
                        }
                        type={showPasswordConfirm ? 'text' : 'password'}
                        name={'passwordConfirm'}
                        icon={'eye'}
                        handlerChange={handleChange}
                        handlerBlur={handleBlur}
                        handlerIcon={() => {
                            setShowPasswordConfirm(!showPasswordConfirm);
                        }}
                        value={values.passwordConfirm}
                        asErrors={errors.passwordConfirm}
                        asTouched={touched.passwordConfirm}
                        asSubmitError={submitErrors.passwordConfirm}
                        disabled={!code}
                    />
                    <label className='c-checkbox agreement'>
                        <input
                            className='c-checkbox__input  '
                            type='checkbox'
                            name='checkbox'
                            defaultChecked={agreement}
                            onChange={() => setAgreement(!agreement)}
                        />
                        <span className='c-checkbox__label'>
                            {accountRegistration.ACC_REG_LABEL_AGREEMENT}
                            <a href={usageUrl} target='_blank' rel='noreferrer'>
                                {
                                    accountRegistration.ACC_REG_LABEL_USAGE_AGREEMENT
                                }
                            </a>
                        </span>
                    </label>
                    <button
                        className='main-btn c-btn c-btn--full u-mt-large'
                        disabled={
                            code === '' ||
                            values.password === '' ||
                            values.passwordConfirm === '' ||
                            values.password !== values.passwordConfirm ||
                            !agreement ||
                            !!Object.keys(errors).length ||
                            loading
                        }
                        type={'submit'}
                    >
                        <span className='c-btn__text'>
                            {loading ? (
                                <Spinner />
                            ) : (
                                accountRegistration.ACC_REG_LABEL_CONTINUE
                            )}
                        </span>
                    </button>
                </form>
            </div>
        </BaseTemplate>
    );
};

export default AccountRegistration;
